import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const PhotoUploadDialog = ({ open, onClose, onSave, galleryId }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [photoData, setPhotoData] = useState({
    title: '',
    description: '',
    isPublic: true
  });

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files
    const validFiles = files.filter(file => {
      // Check file type
      if (!file.type.match('image.*')) {
        setError('Please select only image files (JPEG, PNG, etc.)');
        return false;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Each image size should be less than 10MB');
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    // Create previews
    const newPreviews = validFiles.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
    
    setPreviews(prev => [...prev, ...newPreviews]);
    setError(null); // Clear any previous errors when files are successfully selected
  };

  const handleRemoveFile = (index) => {
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(previews[index].url);
    
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPhotoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setPhotoData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    if (!galleryId) {
      setError('Gallery ID is required');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Add all selected files
      selectedFiles.forEach(file => {
        formData.append('photos', file);
      });
      
      // Add other form data
      formData.append('galleryId', galleryId);
      formData.append('title', photoData.title);
      formData.append('description', photoData.description);
      formData.append('isPublic', photoData.isPublic.toString());

      // Upload files to server
      const response = await api.post('/photos', formData);

      const data = response.data;
      
      // Call the onSave callback with the server response
      onSave(data.data);
      
      // Reset state
      setSelectedFiles([]);
      setPreviews([]);
      setPhotoData({
        title: '',
        description: '',
        isPublic: true
      });
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Clean up previews to avoid memory leaks
    previews.forEach(preview => URL.revokeObjectURL(preview.url));
    
    // Reset state
    setSelectedFiles([]);
    setPreviews([]);
    setPhotoData({
      title: '',
      description: '',
      isPublic: true
    });
    setError(null);
    
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Photos to Gallery</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Photo upload area */}
          <Grid size={{ xs: 12 }}>
            <Box 
              sx={{ 
                border: '2px dashed #ccc', 
                borderRadius: 2, 
                p: 3, 
                textAlign: 'center',
                backgroundColor: '#f8f8f8',
                mb: 2
              }}
            >
              {previews.length > 0 ? (
                <Grid container spacing={2}>
                  {previews.map((preview, index) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                      <Box sx={{ position: 'relative' }}>
                        <img 
                          src={preview.url} 
                          alt={`Preview ${index}`}
                          style={{ 
                            width: '100%', 
                            height: 150, 
                            objectFit: 'cover',
                            borderRadius: 4
                          }}
                        />
                        <IconButton
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ 
                        height: 150, 
                        width: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 40, mb: 1 }} />
                      Add More
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={isUploading}
                >
                  Select Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleFileSelect}
                  />
                </Button>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Supported formats: JPEG, PNG, GIF. Max size: 10MB per image.
              </Typography>
            </Box>
          </Grid>
          
          {/* Photo details */}
          <Grid size={{ xs: 12 }}>
            <TextField
              name="title"
              label="Title (optional)"
              value={photoData.title}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              helperText={`${photoData.title.length}/100 characters`}
              inputProps={{ maxLength: 100 }}
            />
            
            <TextField
              name="description"
              label="Description (optional)"
              value={photoData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              margin="normal"
              helperText={`${photoData.description.length}/500 characters`}
              inputProps={{ maxLength: 500 }}
            />
            
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="isPublic"
                    checked={photoData.isPublic}
                    onChange={handleSwitchChange}
                    color="primary"
                  />
                }
                label={
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    {photoData.isPublic ? (
                      <>
                        <PublicIcon sx={{ mr: 1 }} />
                        Public Photo
                      </>
                    ) : (
                      <>
                        <LockIcon sx={{ mr: 1 }} />
                        Private Photo
                      </>
                    )}
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {photoData.isPublic 
                  ? "Anyone who can view this gallery can see this photo" 
                  : "Only you can see this photo"}
              </Typography>
            </Box>
          </Grid>
          
          {error && (
            <Grid size={{ xs: 12 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={selectedFiles.length === 0 || isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Upload Photos'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoUploadDialog;
